import {React, useContext, useEffect} from "react";
import { useLocation } from 'react-router-dom';
import UserContext from '../../contexts/UserContext';
import authenticate from '../../utils/Authenticate';
import { useNavigate } from "react-router-dom";
import { PO_EVENT_TITLE, PO_BILL_TO_NAME, PO_DELIVERY_ADDRESS_LINES } from "../../utils/poBranding";
import { formatMoney } from "../../utils/currency";

const MasterPurchaseOrderPrint = () => {

  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const { poData } = location.state;

function calculateTotalPrice(purchaseOrder) {
    let total = 0;

    if (!purchaseOrder || !Array.isArray(purchaseOrder.pos)) {
        return total;
    }

    purchaseOrder.pos.forEach(po => {
        if (Array.isArray(po.items)) {
            po.items.forEach(item => {
                total += (item.price || 0) * (item.quantity || 0);
            });
        }
    });

    return total;
}

const total = calculateTotalPrice(poData);

  useEffect( () => {
    async function startup(){
      if(!user){
        await authenticate(setUser,navigate);
      }
    }
    startup();
  },[user])

  const supplier = poData.pos[0]?.supplier;
  const hasAccountDetails = supplier?.accountDetails?.accountNo || supplier?.accountDetails?.name || supplier?.accountDetails?.ifsc;

  const handlePrint = () => {
  const printContents = document.getElementById("po-print-area").innerHTML;
  const printWindow = window.open('', '', 'width=800,height=600');
  printWindow.document.write(`
    <html>
      <head>
        <title>Print Purchase Order</title>
        <style>
          @page { size: A4; margin: 10mm; }
          body {
            font-family: sans-serif;
            margin: 0;
            padding: 0;
          }
          .a4-page {
            width: 210mm;
            min-height: 297mm;
            padding: 10mm;
            margin: auto;
            background: white;
          }
          table, th, td {
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #ccc;
            padding: 8px;
            font-size: 12px;
          }


    h1 {
      font-size: 1.75rem;
      font-weight: bold;
      text-align: center;
      margin-bottom: 1rem;
    }

    .flex {
      display: flex;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    .big-name {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      justify-items: center;
      margin-bottom: 1rem;
    }

    .text-sm {
      font-size: 0.875rem;
    }

    .font-bold {
      font-weight: bold;
    }

    .font-semibold {
      font-weight: 600;
    }

    .mb-4 {
      margin-bottom: 1rem;
    }

    .mb-20 {
      margin-bottom: 5rem;
    }

    .mt-2 {
      margin-top: 0.5rem;
    }

    .mt-8 {
      margin-top: 2rem;
    }

    .mt-10 {
      margin-top: 2.5rem;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 1rem;
      font-size: 0.875rem;
    }

    th, td {
      border: 1px solid #ccc;
      padding: 0.5rem;
    }

    th {
      background-color: #f7f7f7;
    }

    .text-center {
      text-align: center;
    }

    .text-right {
      text-align: right;
    }

    ul {
      padding-left: 1.25rem;
    }

    ul li {
      list-style-type: disc;
    }

    .bank-details {
      border: 1px solid #ccc;
      padding: 0.75rem;
      margin-top: 1rem;
      font-size: 0.875rem;
    }
        </style>
      </head>
      <body onload="window.print(); window.close();">
        ${printContents}
      </body>
    </html>
  `);
  printWindow.document.close();

  // Wait until the new window has loaded, then print
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
};


  return (
    <div>
      <button onClick={() => navigate(-1)} className="text-xl">←</button>
        <button
          onClick={handlePrint}
          className="no-print px-4 py-2 bg-blue-600 text-white rounded mb-4 ml-[50%] -translate-x-[50%] mt-5"
        >
          Print PO
        </button>

       <div id="po-print-area" className="a4-page max-w-4xl mx-auto p-4 border border-gray-300 shadow-lg bg-white">

         <h1 className="text-2xl font-bold text-center uppercase">Purchase Order</h1>
         <h3 className="text-center mb-8 text-sm">{PO_EVENT_TITLE}</h3>

         <div className="big-name grid grid-cols-2 justify-items-center mb-4 mt-8">
           <div>
             <h2 className="font-semibold text-sm mb-4">Bill To:</h2>
             <p className="font-bold">{PO_BILL_TO_NAME}</p>
           </div>

           <div>
             <h2 className="font-semibold text-sm mb-4">To:</h2>
             <p className="font-semibold">{poData.pos[0].supplier.company.toUpperCase()}</p>
             <p className="mt-2">{poData.pos[0].supplier.name}</p>
             <p className="mt-2">Delivery Address:</p>
             {PO_DELIVERY_ADDRESS_LINES.map((line) => <p key={line}>{line}</p>)}
             {poData.pos[0].supplier.contact && <p className="mt-2">Contact: {poData.pos[0].supplier.contact}</p>}
           </div>
         </div>

         <div className="flex justify-between mb-4 text-sm mt-10">
           <div>
             <p><strong>PO ID:</strong> #{poData.PONumber}</p>
             <p><strong>Order Date:</strong> {new Date(poData.pos[0].date).toLocaleDateString()}</p>
           </div>
           <div>
             <p><strong>Delivery Date:</strong> {new Date(poData.pos[0].deliveryDate).toLocaleDateString()}</p>
           </div>
         </div>

         {poData.pos.map((po) => (
            <div key={po._id}>
                <div className="flex justify-between py-2 px-3 text-sm">
                        <p><strong>Delivery To Zone: </strong>{po.zone.desc}</p>
                        <p><strong>Address: </strong> {po.zone.address}</p>
                        <p><strong>PO Incharge: </strong> {po.poIncharge || ''}</p>
                </div>
                <table className="w-full border text-sm mb-4">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2">Sl. No.</th>
                      <th className="border p-2">Item Description</th>
                      <th className="border p-2">Category</th>
                      <th className="border p-2">Quantity</th>
                      <th className="border p-2">Unit</th>
                      <th className="border p-2">Rate</th>
                      <th className="border p-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {po.items.map((item, idx) => (
                      <tr key={item._id}>
                        <td className="border p-2 text-center">{idx + 1}</td>
                        <td className="border p-2">{item.item.name}</td>
                        <td className="border p-2">{item.item.category}</td>
                        <td className="border p-2 text-center">{item.quantity}</td>
                        <td className="border p-2 text-center">{item.item.unit}</td>
                        <td className="border p-2 text-right">{formatMoney(user, item.price)}</td>
                        <td className="border p-2 text-right">{formatMoney(user, item.price*item.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {po.remarks && (
                  <p className="text-sm mb-4 px-1"><strong>Remarks:</strong> {po.remarks}</p>
                )}
            </div>
         ) )}


         <div className="text-right">
           <p className="font-bold text-lg mt-2"><strong>Total:</strong> {formatMoney(user, total)}</p>
         </div>

         {hasAccountDetails && (
           <div className="bank-details mt-8 text-sm border border-gray-300 p-3">
             <p className="font-bold mb-2">Supplier Bank Account Details</p>
             {supplier.accountDetails.name && <p><strong>Account Name:</strong> {supplier.accountDetails.name}</p>}
             {supplier.accountDetails.accountNo && <p><strong>Account No:</strong> {supplier.accountDetails.accountNo}</p>}
             {supplier.accountDetails.ifsc && <p><strong>Sort Code / IFSC:</strong> {supplier.accountDetails.ifsc}</p>}
           </div>
         )}

          <div className="text-right mt-10 text-sm">
          <p className="font-bold mb-20">Rowdell Road Properties Limited</p>
          <p>Authorised Signatory</p>
         </div>

         <div className="mt-8">
           <p className="text-sm">This is a computer-generated document and does not require a signature.</p>
         </div>
       </div>
    </div>

  );
};

export default MasterPurchaseOrderPrint;
